import type { CashFlowDayForecast } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Table } from '@shared/ui/Table';
import { formatDateRu, formatMoney } from '../financierFormat';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionHeaderSub,
  TableWrap,
  CashGapTr,
  DateTd,
  InflowsTd,
  OutflowsTd,
  NetFlowTd,
  ClosingBalanceTd,
  EmptyTableNoticeTd,
} from './styled';

interface CashForecastSectionProps {
  forecast: CashFlowDayForecast[] | undefined;
  /** Резерв на расчётных счетах: остаток ниже него — «ниже резерва». */
  cashBuffer: number;
}

/** «5 млн ₽» — порог резерва словами, без лишних нулей после запятой. */
function bufferLabel(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн ₽`;
  }
  return formatMoney(value);
}

export function CashForecastSection({ forecast, cashBuffer }: CashForecastSectionProps) {
  const buffer = bufferLabel(cashBuffer);
  return (
    <Section>
      <SectionHeader>
        <SectionTitle>30-дневный платёжный календарь и прогноз ликвидности</SectionTitle>
        <SectionHeaderSub>
          Старт — доступный остаток на расчётных счетах всех компаний, без спецсчетов, депозитов
          и налогового резерва. Дальше по дням: план поступлений (КС-3) и план списаний (платежи,
          ФОТ, налоги). Кассовый разрыв — прогноз остатка меньше нуля; ниже резерва — меньше{' '}
          {buffer}.
        </SectionHeaderSub>
      </SectionHeader>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Дата</th>
              <th>Входящий остаток</th>
              <th>План прихода (КС-3/авансы)</th>
              <th>План расхода (заявки/ФОТ)</th>
              <th>Чистый поток</th>
              <th>Прогноз остатка</th>
              <th>Статус ликвидности</th>
            </tr>
          </thead>
          <tbody>
            {forecast && forecast.length > 0 ? (
              forecast.map((day, idx) => {
                const net = day.planned_inflows - day.planned_outflows;
                return (
                  <CashGapTr key={idx} $level={day.cash_level}>
                    <DateTd>{formatDateRu(day.date)}</DateTd>
                    <td>{formatMoney(day.opening_balance)}</td>
                    <InflowsTd>
                      +{formatMoney(day.planned_inflows)}
                    </InflowsTd>
                    <OutflowsTd>
                      −{formatMoney(day.planned_outflows)}
                    </OutflowsTd>
                    <NetFlowTd $positive={net >= 0}>
                      {net >= 0 ? `+${formatMoney(net)}` : formatMoney(net)}
                    </NetFlowTd>
                    <ClosingBalanceTd>{formatMoney(day.closing_balance)}</ClosingBalanceTd>
                    <td>
                      {day.cash_level === 'gap' ? (
                        <Badge $tone="danger">Кассовый разрыв</Badge>
                      ) : day.cash_level === 'below_buffer' ? (
                        <Badge $tone="warning">Ниже резерва {buffer}</Badge>
                      ) : (
                        <Badge $tone="success">Норма</Badge>
                      )}
                    </td>
                  </CashGapTr>
                );
              })
            ) : (
              <tr>
                <EmptyTableNoticeTd colSpan={7}>
                  Нет данных календаря
                </EmptyTableNoticeTd>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
